
import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { HeroSection } from '../components/HeroSection';
import { ScrollSequenceSection } from '../components/ScrollSequenceSection';
import { CinematicHeader } from '../components/CinematicHeader';
import { useSEO, seoConfigs } from '../hooks/useSEO';

// Lazy load components with chunk names for better code splitting
const PhotoGrid = lazy(() => import(/* webpackChunkName: "photo-grid" */ '../components/PhotoGrid'));
const FestivalBannerSlider = lazy(() => import(/* webpackChunkName: "festival-slider" */ '../components/FestivalBannerSlider'));
const BehindTheScenesSlider = lazy(() => import(/* webpackChunkName: "behind-scenes-slider" */ '../components/BehindTheScenesSlider'));

import { unsortedPosters, servicesData, experienceHighlights } from '../constants/homepage';

// Function to extract year from filename
const extractYear = (filename: string): number => {
  // Handle filenames with format "(YYYY) Title" or "YYYY, Title"
  const yearMatch = filename.match(/(?:\()?(\d{4})(?:\)|,)?/);
  return yearMatch ? parseInt(yearMatch[1], 10) : 0;
};

export const HomePage: React.FC = () => {
  useSEO(seoConfigs.home);

  // Memoize film posters data to prevent re-sorting on every render
  const sortedFilmPosters = useMemo(() => {
    return [...unsortedPosters].sort((a, b) => {
      const yearA = extractYear(a.src);
      const yearB = extractYear(b.src);
      return yearB - yearA; // Sort in descending order (newest first)
    });
  }, []);

  return (
    <>
      <CinematicHeader />

      <ScrollSequenceSection
        id="featured-work"
        backgroundClassName="bg-gradient-to-b from-black/40 via-zinc-900 to-black/40"
      >
        <motion.div
          className="max-w-full mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, margin: "100px" }}
        >
          <Suspense fallback={
            <div className="py-16 flex justify-center items-center">
              <div className="animate-pulse bg-zinc-800 rounded-3xl h-96 w-full max-w-6xl"></div>
            </div>
          }>
            <PhotoGrid
              images={sortedFilmPosters}
              initialVisibleCount={6} // Show 1 row of 6 by default on large screens
            />
          </Suspense>
        </motion.div>
      </ScrollSequenceSection>

      <ScrollSequenceSection backgroundClassName="bg-gradient-to-b from-zinc-900 via-zinc-950 to-black/40">
        <motion.div
          className="max-w-7xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, margin: "100px" }}
        >
          <motion.h2
            className="text-4xl sm:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-sky-400 to-blue-400 bg-clip-text text-transparent"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            viewport={{ once: true, margin: "100px" }}
          >
            Services & Expertise
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {servicesData.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-zinc-800/80 backdrop-blur p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 group cursor-pointer border border-white/5"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 * (index + 1) }}
                viewport={{ once: true, margin: "100px" }}
                style={{ willChange: 'transform, opacity' }}
              >
                <div className="text-5xl mb-4 text-sky-400 group-hover:scale-110 transition-transform duration-300">{service.icon}</div>
                <h3 className="text-3xl font-bold text-sky-400 mb-4">{service.title}</h3>
                <p className="text-gray-300 text-lg">
                  {service.text}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </ScrollSequenceSection>

      <ScrollSequenceSection
        backgroundClassName="bg-[radial-gradient(circle_at_top,#0f172a33,transparent_60%)]"
      >
        <motion.div
          className="max-w-5xl mx-auto text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, margin: "100px" }}
        >
          <motion.p
            className="uppercase tracking-[0.4em] text-xs text-sky-300/70 mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            viewport={{ once: true, margin: "100px" }}
          >
            Sonic Storytelling Process
          </motion.p>
          <motion.h2
            className="text-4xl sm:text-5xl font-semibold text-white mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            viewport={{ once: true, margin: "100px" }}
          >
            Each scroll reveals another layer of how music, texture, and rhythm carry a story forward.
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {experienceHighlights.map((highlight, index) => (
              <motion.div
                key={highlight.title}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm text-left h-full"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * (index + 1) }}
                viewport={{ once: true, margin: "100px" }}
                style={{ willChange: 'transform, opacity' }}
              >
                <p className="text-xs uppercase tracking-[0.4em] text-sky-200/60 mb-4">{highlight.metric}</p>
                <h3 className="text-2xl font-semibold text-white mb-3">{highlight.title}</h3>
                <p className="text-gray-300 leading-relaxed">{highlight.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </ScrollSequenceSection>

      <Suspense fallback={
        <div className="py-16 text-center">
          <div className="animate-pulse bg-zinc-800 rounded-3xl h-96 w-full max-w-6xl mx-auto"></div>
        </div>
      }>
        <FestivalBannerSlider />
      </Suspense>

      <Suspense fallback={
        <div className="py-16 text-center">
          <div className="animate-pulse bg-zinc-800 rounded-3xl h-96 w-full max-w-6xl mx-auto"></div>
        </div>
      }>
        <BehindTheScenesSlider />
      </Suspense>

      <HeroSection />
    </>
  );
};
